import axios from 'axios'
import { Message } from 'element-ui'
import { getAccessToken, getTenantId, removeToken, getCompanyInfo } from './auth'
import router from '@/router'

// 创建axios实例
// 开发环境：使用相对路径，通过 vue.config.js 的代理转发到后端
// 生产环境：使用完整的 API 地址
const isDevelopment = process.env.NODE_ENV === 'development'
const apiUrl = process.env.VUE_APP_API_URL || '/admin-api'
const baseURL = isDevelopment
    ? apiUrl  // 开发环境使用相对路径，通过代理转发
    : (process.env.VUE_APP_BASE_URL || '') + apiUrl  // 生产环境使用完整地址
const timeout = process.env.VUE_APP_REQUEST_TIMEOUT || 50000

const service = axios.create({
    baseURL: baseURL || '/', // api 的 base_url
    timeout: parseInt(timeout, 10), // 请求超时时间
    withCredentials: false // 禁用 Cookie 等信息
})

// 请求白名单，无须 token 的接口
const whiteList = ['/system/auth/login', '/system/auth/refresh-token']

// request拦截器
service.interceptors.request.use(
    config => {
        // 是否需要设置 token
        let isToken = (config.headers || {}).isToken !== false
        whiteList.some(v => {
            if (config.url && config.url.indexOf(v) > -1) {
                return (isToken = false)
            }
        })
        // 当需要token且token存在时，在header中添加Authorization
        if (isToken && getAccessToken()) {
            config.headers.Authorization = 'Bearer ' + getAccessToken()
        }
        // 设置租户ID（如果未设置，使用默认值 1）
        const tenantId = getTenantId() || '1'
        config.headers['tenant-id'] = tenantId

        // 获取公司ID并添加到请求参数中
        const companyInfo = getCompanyInfo()
        if (companyInfo && companyInfo.id) {
            const companyId = companyInfo.id
            const method = config.method.toUpperCase()

            // 根据请求方法添加到不同的位置
            if (method === 'GET' || method === 'DELETE') {
                // GET 和 DELETE 请求：添加到 params
                if (!config.params) {
                    config.params = {}
                }
                config.params.companyId = companyId
            } else if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
                // POST、PUT、PATCH 请求：添加到 data
                if (config.data) {
                    // 如果 data 是普通对象，直接添加 companyId
                    if (typeof config.data === 'object' && !Array.isArray(config.data) && !(config.data instanceof FormData)) {
                        config.data.companyId = companyId
                    }
                    // 如果 data 是数组或 FormData，不处理（避免破坏数据结构）
                    // 对于数组类型的 data，可以考虑添加到 URL 参数中
                    if (Array.isArray(config.data) || config.data instanceof FormData) {
                        // 对于数组或 FormData，将 companyId 添加到 URL 参数中
                        if (!config.params) {
                            config.params = {}
                        }
                        config.params.companyId = companyId
                    }
                } else {
                    // 如果 data 不存在，创建对象
                    config.data = { companyId: companyId }
                }
            }
        }

        const method = config.method.toUpperCase()
        // 防止 GET 请求缓存
        if (method === 'GET') {
            config.headers['Cache-Control'] = 'no-cache'
            config.headers['Pragma'] = 'no-cache'
        }
        return config
    },
    error => {
        console.log(error) // for debug
        Promise.reject(error)
    }
)

// response拦截器
service.interceptors.response.use(
    response => {
        const res = response.data
        // 如果返回的状态码不是200，则认为是错误
        if (res.code !== undefined && res.code !== 0 && res.code !== 200) {
            Message({
                message: res.msg || res.message || 'Error',
                type: 'error',
                duration: 5 * 1000
            })
            // 50008: 非法的token; 50012: 其他客户端登录了; 50014: Token 过期了;
            if (res.code === 401 || res.code === 50008 || res.code === 50012 || res.code === 50014) {
                // 重新登录
                removeToken()
                router.push('/login')
            }
            return Promise.reject(new Error(res.msg || res.message || 'Error'))
        } else {
            // 成功时返回后端数据（res 是 { code, data, msg } 格式）
            // 如果 res.data 存在，返回 res.data；否则返回整个 res
            return res.data !== undefined ? res.data : res
        }
    },
    error => {
        console.log('err' + error) // for debug
        let message = '请求失败'

        // 处理不同类型的错误
        if (error.code === 'ECONNABORTED' || error.message && error.message.includes('timeout')) {
            message = '请求超时，请稍后重试'
        } else if (error.message === 'Network Error') {
            message = '网络错误，请检查网络连接'
        } else if (error.response) {
            message = error.response.data?.msg || error.response.data?.message || error.message || '请求失败'
        } else if (error.message) {
            message = error.message
        }

        Message({
            message: message,
            type: 'error',
            duration: 5 * 1000
        })
        return Promise.reject(error)
    }
)

export default service

